import React, { createContext, useState, useEffect, useContext } from 'react';
import { registrationAPI, competitionAPI, workshopAPI } from '../services/api';
import { useAuth } from './AuthContext';

// Create context
const RegistrationContext = createContext();

// Registration provider component
export const RegistrationProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [registration, setRegistration] = useState(null);
  const [competitions, setCompetitions] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user registration when user is logged in
  useEffect(() => {
    const fetchRegistration = async () => {
      if (!currentUser) {
        setRegistration(null);
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const response = await registrationAPI.getMyRegistration();
        setRegistration(response.data.registration);
      } catch (err) {
        // User might not have a registration yet
        setRegistration(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchRegistration();
  }, [currentUser]);

  // Fetch competitions
  const fetchCompetitions = async () => {
    try {
      setLoading(true);
      const response = await competitionAPI.getCompetitions();
      setCompetitions(response.data.competitions);
      return response.data.competitions;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Fetch workshops
  const fetchWorkshops = async () => {
    try {
      setLoading(true);
      const response = await workshopAPI.getWorkshops();
      setWorkshops(response.data.workshops);
      return response.data.workshops;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Create registration
  const createRegistration = async (registrationData) => {
    try {
      setError(null);
      setLoading(true);
      const response = await registrationAPI.createRegistration(registrationData);
      
      // Fetch updated registration
      await fetchUserRegistration();
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Fetch user registration
  const fetchUserRegistration = async () => {
    try {
      setLoading(true);
      const response = await registrationAPI.getMyRegistration();
      setRegistration(response.data.registration);
      return response.data.registration;
    } catch (err) {
      setRegistration(null);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update profile and WSDC information
  const updateProfile = async (profileData) => {
    try {
      setError(null);
      setLoading(true);
      const response = await registrationAPI.updateProfile(profileData);
      
      // Fetch updated registration
      await fetchUserRegistration();
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Accept partner request
  const acceptPartner = async (partnerData) => {
    try {
      setError(null);
      setLoading(true);
      const response = await registrationAPI.acceptPartner(partnerData);
      
      // Fetch updated registration
      await fetchUserRegistration();
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Register for competition
  const registerForCompetition = async (competitionData) => {
    try {
      setError(null);
      setLoading(true);
      const response = await competitionAPI.registerForCompetition(competitionData);
      
      // Fetch updated registration
      await fetchUserRegistration();
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Accept competition partner request
  const acceptCompetitionPartner = async (partnerData) => {
    try {
      setError(null);
      setLoading(true);
      const response = await competitionAPI.acceptPartner(partnerData);
      
      // Fetch updated registration
      await fetchUserRegistration();
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Cancel competition registration
  const cancelCompetitionRegistration = async (id) => {
    try {
      setError(null);
      setLoading(true);
      const response = await competitionAPI.cancelRegistration(id);
      
      // Fetch updated registration
      await fetchUserRegistration();
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Register for workshop
  const registerForWorkshop = async (workshopData) => {
    try {
      setError(null);
      setLoading(true);
      const response = await workshopAPI.registerForWorkshop(workshopData);
      
      // Fetch updated registration and workshops
      await Promise.all([
        fetchUserRegistration(),
        fetchWorkshops()
      ]);
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Cancel workshop registration
  const cancelWorkshopRegistration = async (id) => {
    try {
      setError(null);
      setLoading(true);
      const response = await workshopAPI.cancelRegistration(id);
      
      // Fetch updated registration and workshops
      await Promise.all([
        fetchUserRegistration(),
        fetchWorkshops()
      ]);
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Context value
  const value = {
    registration,
    competitions,
    workshops,
    loading,
    error,
    fetchCompetitions,
    fetchWorkshops,
    createRegistration,
    fetchUserRegistration,
    updateProfile,
    acceptPartner,
    registerForCompetition,
    acceptCompetitionPartner,
    cancelCompetitionRegistration,
    registerForWorkshop,
    cancelWorkshopRegistration,
  };

  return (
    <RegistrationContext.Provider value={value}>
      {children}
    </RegistrationContext.Provider>
  );
};

// Custom hook to use registration context
export const useRegistration = () => {
  const context = useContext(RegistrationContext);
  if (!context) {
    throw new Error('useRegistration must be used within a RegistrationProvider');
  }
  return context;
};

export default RegistrationContext;
